import { Pressable, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import type { TodaySession } from "@heraion/shared";

import { Button } from "../primitives/Button";
import type { UiTheme } from "../theme";
import { makeHeroStyles } from "./homeHeroStyles";

// Action-first Home hero at the top of the Plan tab (redesign 2026-06-04):
// the athlete's first tap of the day is "start today's session". When an
// active program resolves a next session we show it with one large Start
// CTA; otherwise we fall back to a blank-state that still gets them training
// in one tap. Programs / plans live below the hero, demoted. Lifted from
// apps/mobile into @heraion/ui so mobile + the web-rnw console render one
// tree; name resolution + theme are host-provided. On hosts with no logger
// (web), onStartToday routes to a log-on-mobile seam — the CTA stays so the
// surface looks identical.
type Props = {
  today: TodaySession | null;
  resolveName: (slug: string, snapshot?: string) => string;
  theme: UiTheme;
  loading: boolean;
  isStarting: boolean;
  onStartToday: () => void;
  onNewWorkout: () => void;
  /** #120: the blank state points at the ready programs in the library.
   *  The host opens its Create hub (which carries the Library); absent = no
   *  button, and the sentence still reads true. */
  onOpenLibrary?: () => void;
  /** AP-9b D3: the same card inside the Create hub, under the sheet's own
   *  «Today» label (so no eyebrow) and above the «New workout» row (so no
   *  «blank workout instead» link repeating it). */
  inSheet?: boolean;
};

export function HomeHero({
  today,
  resolveName,
  theme,
  loading,
  isStarting,
  onStartToday,
  onNewWorkout,
  onOpenLibrary,
  inSheet = false,
}: Props) {
  const { t } = useTranslation();
  const styles = makeHeroStyles(theme);

  if (today === null) {
    return (
      <View style={styles.card}>
        <Text style={styles.eyebrow}>
          {t("mobile.home.eyebrow", { defaultValue: "TODAY" })}
        </Text>
        <Text style={styles.blankTitle}>
          {t("mobile.home.blankTitle", { defaultValue: "Ready to train?" })}
        </Text>
        <Text style={styles.blankBody}>
          {loading
            ? t("mobile.home.blankLoading", {
                defaultValue: "Loading your plan…",
              })
            : t("mobile.home.blankBody", {
                defaultValue:
                  "No active program right now. $t(brand.product) has ready programs: follow one with a tap. Or start a blank workout.",
              })}
        </Text>
        {/* #472: the buttons in the sentence's order, the ready programs first. */}
        {onOpenLibrary ? (
          <Button
            theme={theme}
            label={t("mobile.home.blankLibrary", {
              defaultValue: "Browse ready programs",
            })}
            onPress={onOpenLibrary}
            full
          />
        ) : null}
        <Button
          theme={theme}
          variant={onOpenLibrary ? "secondary" : "primary"}
          label={t("mobile.home.newWorkout", { defaultValue: "New workout" })}
          onPress={onNewWorkout}
          full
        />
      </View>
    );
  }

  const { resolved, weekIdx } = today;
  // First few movements as a glanceable preview line.
  const preview = resolved.entries
    .slice(0, 3)
    .map((e) => resolveName(e.exerciseId))
    .join(" · ");
  const more = resolved.entries.length - 3;

  return (
    <View style={styles.card}>
      {inSheet ? null : (
        <Text style={styles.eyebrow}>
          {t("mobile.home.eyebrow", { defaultValue: "TODAY" })}
        </Text>
      )}
      <Text style={styles.sessionName} numberOfLines={2}>
        {resolved.sessionName}
      </Text>
      <Text style={styles.meta} numberOfLines={1}>
        {t("mobile.home.programMeta", {
          defaultValue: "{{program}} · Week {{week}}",
          // PE-21 — the RESOLVED name: an i18n interpolation accepts any
          // value, so a per-locale object would have rendered here as
          // "[object Object]" on the home screen with nothing to catch it.
          program: resolved.programName,
          week: weekIdx + 1,
        })}
      </Text>
      {preview ? (
        <Text style={styles.preview} numberOfLines={2}>
          {preview}
          {more > 0
            ? t("mobile.home.morePreview", {
                defaultValue: " +{{count}} more",
                count: more,
              })
            : ""}
        </Text>
      ) : null}
      <Button
        theme={theme}
        label={t("mobile.home.startToday", { defaultValue: "▶ Start session" })}
        onPress={onStartToday}
        loading={isStarting}
        full
      />
      {inSheet ? null : (
        <Pressable
          accessibilityRole="button"
          onPress={onNewWorkout}
          style={({ pressed }) => [
            styles.linkBtn,
            pressed && styles.linkBtnPressed,
          ]}
        >
          <Text style={styles.linkText}>
            {t("mobile.home.blankInstead", {
              defaultValue: "Start a blank workout instead",
            })}
          </Text>
        </Pressable>
      )}
    </View>
  );
}
